// The Artisan Pillar (§31 A6): the Pillar of Excellence and the Mark of
// Excellence on a Monarch.
//
// While both stand under the side (`hasPower(…, "artisan")`, mirrored to
// `Position.artisans`): every piece of the side +1 max HP (`maxHealthOf`),
// regeneration 2 (`regenerate`), upgrades one cheaper on their dearest row
// (`upgradeBill`), and a Monarch's step reaches `ARTISAN.reach` further
// (`kingReach`).
//
// **The loss** (`settleArtisan`, one diff in `applyCommand` before
// `syncGear`, every door at once): the Pillar falls or the Mark's bearer
// does. Then the bearer dies, the Pillar comes down, and the side's
// `Position.frailty` goes up one — −1 max HP forever, never below 1. The
// Mark **never drops and is never stolen**: losing it destroys it. Any loss
// retires the artifact for the run (`artisanRetired`), and at most one
// Pillar stands on the map (`artisanPlaceError`).

import { pieceColor, type Side } from "../board";
import type { GameState } from "../commands";
import { gearOf } from "../industry/gear";
import { ARTISAN_PILLAR, cloneIndustry, type IndustryState, structureAt } from "../industry/state";
import { wreckStructure } from "../industry/structures";
import {
  clonePosition,
  hurtAt,
  maxHealthOf,
  pieceAt,
  type Position,
  removePiece,
  setHurt
} from "../position";
import { spillPiece } from "../reduce";
import { kinglessGrace } from "./despair";

/** Has an Artisan Pillar ever been lost on this board? Then it is gone for the run. */
export const artisanRetired = (position: Position): boolean => position.frailty.some((n) => n > 0);

/** Why `kind` may not be placed now for the Pillar's own rules, or null. */
export function artisanPlaceError(position: Position, industry: IndustryState): string | null {
  if (artisanRetired(position)) return "the Pillar of Excellence is lost for this run";
  for (const structure of industry.structures.values()) {
    if (structure.kind === ARTISAN_PILLAR) return "only one Pillar of Excellence may stand";
  }
  return null;
}

/** The square the body `id` stands on, or -1. */
function squareOf(position: Position, id: number): number {
  for (const [sq, at] of position.pieceId) if (at === id) return sq;
  return -1;
}

/**
 * The loss, off `before` and `next`: every Mark borne before whose Pillar or
 * bearer is gone after. Returns `next` itself when nothing was lost.
 */
export function settleArtisan(before: GameState, next: GameState): GameState {
  const lost = gearOf(before.industry).filter((gear) => {
    if (gear.kind !== ARTISAN_PILLAR || gear.holder === 0) return false;
    const stands = structureAt(next.industry, gear.anchor)?.kind === ARTISAN_PILLAR;
    return !stands || squareOf(next.position, gear.holder) < 0;
  });
  if (lost.length === 0) return next;

  const position = clonePosition(next.position);
  const industry = cloneIndustry(next.industry);
  const frailty = [...position.frailty];
  for (const gear of lost) {
    const side = gear.origin;
    // The bearer is destroyed — a Monarch, so it may be the side's last.
    const bearer = squareOf(position, gear.holder);
    if (bearer >= 0) {
      const piece = pieceAt(position, bearer);
      spillPiece(industry, bearer, piece);
      setHurt(position, bearer, 0);
      removePiece(position, bearer);
    }
    // The Pillar comes down with it (diamond: nothing drops).
    if (structureAt(industry, gear.anchor)?.kind === ARTISAN_PILLAR) wreckStructure(position, industry, gear.anchor);
    while (frailty.length <= side) frailty.push(0);
    frailty[side]++;
  }
  position.frailty = frailty;
  // The Pillar is off now too, so the mirror's +1 goes with the −1: a wound
  // deeper than the new maximum leaves the piece at 1 HP, never dead.
  position.artisans = position.artisans.filter((side) => !lost.some((gear) => gear.origin === side));
  for (const [sq, piece] of [...position.board].sort((a, b) => a[0] - b[0])) {
    if (!lost.some((gear) => gear.origin === pieceColor(piece))) continue;
    const max = maxHealthOf(position, sq);
    if (max > 0 && hurtAt(position, sq) >= max) setHurt(position, sq, max - 1);
  }

  const out: GameState = { ...next, position, industry };
  // A side that lost its last Monarch this way has lost (the doc: "you can
  // lose this way") — `holdStatus`'s rule, for the bearer's side.
  for (const gear of lost) {
    const side = gear.origin as Side;
    if (
      position.kings[side] < 0 &&
      position.kings.some((sq, other) => sq >= 0 && other !== side) &&
      !(kinglessGrace(out.run) && position.counts[side] > 0)
    ) {
      out.status = "checkmate";
    }
  }
  return out;
}
